var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Box, Grid, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import * as ls from 'local-storage';
import lodashDebounce from 'lodash.debounce';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import GPT3Tokenizer from 'gpt3-tokenizer';
import moment from 'moment';
// Interal dependencies
import { Editor } from './Editor';
import { documentStyles } from './Document.styles';
import { axiosClient } from '../axios';
import { getAuthHeader, UserContext } from '../auth';
import { ENDPOINTS, LOG_LEVELS, SERVICES, CHAT_ROLES, MAX_INPUT_TOKENS, DEFAULT_EDITOR, DEFAULT_TEMPLATE, CLIENT_ENDPOINTS, DEFAULT_CHAT, CHAT_CONTEXT, } from '../constants';
import { Chat } from '../chat';
import { AI_PROVIDER_TYPES, OUTPUT_TYPES } from '../chat/constants';
import { EditIconSG, ButtonSG, WorkflowIconSG } from '../components';
import { createDocument } from './Documents';
import { Workflows } from '../workflows';
import { ChatTitle, AI_MODELS } from '../chat';
import { chatStyles } from '../chat/Chat.styles';
import { DEFAULT_WORKFLOW } from '../workflows';
/**
 * Auto save for document
 * @param documentId Document Id
 * @param user User credential
 * @returns Success/fail
 */
export const autoSave = (documentId, user) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        // Stop if documentId was not loaded
        if (!documentId) {
            return false;
        }
        ;
        // Stop if user credential was not loaded
        if (!user) {
            return false;
        }
        // Get the latest document from local storage
        const document = JSON.parse(ls.get(documentId) || '{}');
        // Get auth header
        const authHeader = yield getAuthHeader(user);
        const headers = Object.assign({}, authHeader);
        // Save the document to the database
        yield axiosClient.put(`${process.env.SERVER_URL}${ENDPOINTS.DOCUMENT}${ENDPOINTS.UPDATE_DOCUMENT}/${documentId}`, { document }, { headers });
        return true;
    }
    catch (err) {
        // Log error to the log system
        axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
            level: LOG_LEVELS.ERROR,
            logMessage: `Document, autoSave, ${err.message}`,
            metadata: {
                env: process.env.ENV,
                service: SERVICES.DOCUMENT,
                userId: user === null || user === void 0 ? void 0 : user.uid,
                email: user === null || user === void 0 ? void 0 : user.email,
                userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
            },
        });
        return false;
    }
    ;
});
;
/**
 * Document (Chat, Editor, Template, ...)
 * @param props Document props
 * @returns Document Element
 */
export const Document = function (props) {
    // Extract translation function
    const { t, i18n } = useTranslation();
    // Get user credential
    const user = useContext(UserContext);
    // Get search params
    const [searchParams] = useSearchParams();
    // Use navigate
    const navigate = useNavigate();
    // Get documentId
    const documentId = searchParams.get('documentId') || undefined;
    const [chat, setChat] = useState(Object.assign({}, DEFAULT_CHAT));
    const [editor, setEditor] = useState(Object.assign({}, DEFAULT_EDITOR));
    // Fetch and update document when user credential changed
    useEffect(() => {
        const createNewDocument = () => __awaiter(this, void 0, void 0, function* () {
            try {
                // Initialize document
                const document = {
                    chat: Object.assign({}, DEFAULT_CHAT),
                    editor: Object.assign({}, DEFAULT_EDITOR),
                    template: Object.assign({}, DEFAULT_TEMPLATE),
                };
                // Get auth header
                const authHeader = yield getAuthHeader(user);
                const pathName = CLIENT_ENDPOINTS.CHAT;
                // Create new document and navigate to it
                createDocument(user, navigate, authHeader, document, pathName);
            }
            catch (err) {
                // Save the error to the log system
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `Document, createNewDocument, ${err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.DOCUMENT,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
            }
            ;
        });
        const getDocument = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                if (!documentId) {
                    return;
                }
                // Get autheHeader
                const authHeader = yield getAuthHeader(user);
                const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
                // Get document
                const res = yield axiosClient.get(`${process.env.SERVER_URL}${ENDPOINTS.DOCUMENT}${ENDPOINTS.GET_DOCUMENT}/${documentId}`, { headers });
                // Extract document
                const document = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.document;
                if (!document) {
                    createNewDocument();
                }
                else {
                    setChat(Object.assign({}, document.chat));
                    setEditor(Object.assign({}, document.editor));
                    // Write document to local storage
                    ls.set(documentId, JSON.stringify(document));
                }
            }
            catch (err) {
                // Save the error to the log system
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `Document, getDocument, ${err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.DOCUMENT,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
            }
            ;
        });
        try {
            if (user) {
                if (!documentId) {
                    createNewDocument();
                }
                else {
                    getDocument();
                }
            }
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Document, useEffect, getDocument, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.DOCUMENT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        }
        ;
    }, [user, documentId]);
    // Extract output language
    const { outputLanguage } = useOutletContext();
    /** Is Smaller than medium breakpoint */
    const theme = useTheme();
    const isDownMDBreakPoint = useMediaQuery(theme.breakpoints.down('md'));
    const [documentUpdateCount, setDocumentUpdateCount] = useState(0);
    const handleDocumentUpdate = () => {
        setDocumentUpdateCount(documentUpdateCount + 1);
        return true;
    };
    /** Auto save */
    const autoSaveDebounce = useCallback(lodashDebounce(autoSave, 1000), []);
    // Auto save document when document changed
    useEffect(() => {
        try {
            // Stop if documentId was not loaded
            if (!documentId) {
                return;
            }
            autoSaveDebounce(documentId, user);
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Document, useEffect, autoSaveDebounce, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.DOCUMENT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        }
        ;
    }, [documentUpdateCount]);
    // Waiting chat response state
    const [/** Waiting chat reponse state */ waiting, setWaiting] = useState(false);
    const [/** Chat response content */ resContent, setResContent] = useState('');
    /**
     * Send chat message
     * @param message Message will be sent to a ai model
     * @param metadata Metadata of the message
     * @param brandVoice Brand voice of the message
     * @param attachFiles List of attached files
     * @return Promise of success/fail
     */
    const sendMessage = useCallback((message, metadata, brandVoice, attachFiles = [], chatContext = CHAT_CONTEXT.CHAT) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        const startTimeInSecond = moment().valueOf() / 1000;
        // Get AI model
        let aiModel = user ? ((chat === null || chat === void 0 ? void 0 : chat.aiModel) || AI_MODELS.aiSearch.modelValue) : AI_MODELS.incognito.modelValue;
        if (chatContext === CHAT_CONTEXT.WORKFLOW) {
            aiModel = AI_MODELS.gpt4O.modelValue;
        }
        const /** Model metadata */ modelMetadata = AI_MODELS[aiModel] || AI_MODELS.aiSearch;
        try {
            if (isDownMDBreakPoint) {
                // If screen size smaller then medium
                setShowEditor(false);
                setShowWorkflow(false);
            }
            if (message.trim().length === 0) {
                // If message is empty, stop it
                return undefined;
            }
            if ((modelMetadata === null || modelMetadata === void 0 ? void 0 : modelMetadata.aiProviderType) === AI_PROVIDER_TYPES.AWS) {
                // If it's a AWS Bedrock model
                // Add user's new message
                chat.messages.push({
                    role: CHAT_ROLES.USER,
                    content: [{ text: message.trim() }],
                    metadata,
                    createdAt: (new Date()).toISOString(),
                    attachFiles,
                });
            }
            else {
                // If it's a Azure OpenAI model
                // Add user's new message
                chat.messages.push({
                    role: CHAT_ROLES.USER,
                    content: [{ type: 'text', text: message.trim() }],
                    metadata,
                    createdAt: (new Date()).toISOString(),
                    attachFiles,
                });
            }
            // Max input tokens for chat API
            // 5000 tokens for tools
            const /** Max Input Tokens for AI API */ maxInputTokens = MAX_INPUT_TOKENS[aiModel] || MAX_INPUT_TOKENS.gpt35Turbo;
            // Messages length
            const messagesLength = chat.messages.length;
            // Total token
            let inputTokens = 0;
            // Initialize the tokenizer
            const tokenizer = new GPT3Tokenizer({ type: 'gpt3' });
            if (brandVoice && brandVoice.length > 0) {
                // Add brand voice tokens
                inputTokens += tokenizer.encode(brandVoice).bpe.length;
            }
            // {, "role", "content", }, ","
            const tokensPerMessage = 5;
            // Create description
            const /** List of messages */ description = [];
            const workflowStepIdx = (metadata === null || metadata === void 0 ? void 0 : metadata.workflowStepIdx) || 0;
            const maxMessagesStack = 50;
            for (let i = messagesLength - 1; i >= 0; i--) {
                // Get message
                const message = chat.messages[i];
                // Extract content from Azure or AWS models
                // Azure models: type of content is string
                // AWS models: type of content is [{text: ....}, {}]
                const content = typeof (message.content) === 'string' ? message.content : message.content[0].text;
                // Get message's tokens
                const messageTokens = tokensPerMessage + tokenizer.encode(message.role).bpe.length + tokenizer.encode(content).bpe.length;
                if (i === messagesLength - 1) {
                    // Handle current message
                    // Notify user if current message too long
                    if (messageTokens > maxInputTokens) {
                        toast.error(t('The message exceeds the limit. Please try a shorter one.'));
                        return undefined;
                    }
                    ;
                    // Add current message to description
                    // Dont need to add metadata
                    description.unshift({
                        role: message.role,
                        content: message.content,
                        metadata,
                        attachFiles: message.attachFiles,
                    });
                    inputTokens += messageTokens;
                }
                else if (chatContext === CHAT_CONTEXT.WORKFLOW && i >= (messagesLength - (workflowStepIdx * 2 + 1))
                    || (chatContext === CHAT_CONTEXT.CHAT
                        && (messageTokens + inputTokens < maxInputTokens)
                        && ((messagesLength - i) < maxMessagesStack))) {
                    // Add message if inputTokens not exceed acceptable number
                    // Dont need to add metadata
                    description.unshift({
                        role: message.role,
                        content: message.content,
                        metadata,
                        attachFiles: message.attachFiles,
                    });
                    inputTokens += messageTokens;
                }
                else {
                    break;
                }
            }
            ;
            // Set waiting and new chat
            setWaiting(true);
            setChat(Object.assign({}, chat));
            let res = undefined;
            if (aiModel === AI_MODELS.incognito.modelValue) {
                // Get send chat with google search response stream
                res = yield fetch(`${process.env.SERVER_URL}${ENDPOINTS.CHAT}${ENDPOINTS.GUEST_SEARCH_ONLINE}`, {
                    method: "post",
                    headers: {
                        'Accept-Language': i18n.language,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        aiModel,
                        description,
                        chatContext,
                        inputTokens,
                        outputLanguage,
                        brandVoice,
                    }), // server is expecting JSON
                });
            }
            else {
                // Get auth header and send chat
                const authHeader = yield getAuthHeader(user);
                // Get send chat response stream
                res = yield fetch(`${process.env.SERVER_URL}${ENDPOINTS.CHAT}${ENDPOINTS.SEND_CHAT}`, {
                    method: "post",
                    headers: Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language, "Content-Type": "application/json" }),
                    body: JSON.stringify({
                        aiModel,
                        description,
                        chatContext,
                        inputTokens,
                        outputLanguage,
                        brandVoice,
                    }), // server is expecting JSON
                });
            }
            if ((res === null || res === void 0 ? void 0 : res.status) !== 200) {
                const resInJson = yield res.json();
                throw ({
                    message: resInJson === null || resInJson === void 0 ? void 0 : resInJson.message,
                    response: {
                        status: resInJson === null || resInJson === void 0 ? void 0 : resInJson.status,
                        type: resInJson === null || resInJson === void 0 ? void 0 : resInJson.type,
                        data: {
                            message: resInJson === null || resInJson === void 0 ? void 0 : resInJson.message,
                        },
                    },
                });
            }
            ;
            // Stream content
            let /** Response stream content */ content = '';
            // Decode the stream content
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            const loopRunner = true;
            while (loopRunner) {
                const { value, done } = yield reader.read();
                if (done) {
                    // If the stream is finished
                    break;
                }
                ;
                // Decode the stream value to text
                const decodedChunk = decoder.decode(value, { stream: true });
                let decodedChunkObj = undefined;
                try {
                    // Parse the string to object
                    decodedChunkObj = JSON.parse(decodedChunk);
                    if ((decodedChunkObj === null || decodedChunkObj === void 0 ? void 0 : decodedChunkObj.status) && decodedChunkObj.status !== 200) {
                        // If error, notify user with the server error message
                        toast.error(t('Something went wrong. We are figuring it out.'));
                        // Update waiting and new chat
                        setWaiting(false);
                        return undefined;
                    }
                    ;
                }
                catch (err) {
                    // If fail to parse, go to below
                }
                ;
                content = '' + content + decodedChunk;
                // Update chat response content
                setResContent(content);
            }
            ;
            if (modelMetadata.aiProviderType === AI_PROVIDER_TYPES.AWS && modelMetadata.outputType === OUTPUT_TYPES.WORDS) {
                // If it's a AWS Bedrock model
                // Add response message to the chat
                chat.messages.push({ role: 'assistant', content: [{ text: content }], createdAt: (new Date()).toISOString() });
            }
            else if (modelMetadata.outputType === OUTPUT_TYPES.IMAGES) {
                const images = JSON.parse(content);
                // If it's AZURE image model
                // Add response message to the chat
                chat.messages.push({ role: 'assistant', content: '', images: images, createdAt: (new Date()).toISOString() });
            }
            else {
                // If it's a Azure OpenAI model
                // Add response message to the chat
                chat.messages.push({ role: 'assistant', content, createdAt: (new Date()).toISOString() });
            }
            ;
            setChat(Object.assign({}, chat));
            // Update waiting and new chat
            setWaiting(false);
            setResContent('');
            const endTimeInSecond = moment().valueOf() / 1000;
            const apiResponseTime = endTimeInSecond - startTimeInSecond;
            // Record metric to newrelic
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.RECORD_METRIC}`, {
                name: 'Document/sendMessage',
                value: apiResponseTime,
            });
            // Increment metric to newrelic
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.INCREMENT_METRIC}`, {
                name: 'Document/sendMessage/success',
                value: 1,
            });
            return content;
        }
        catch (err) {
            // Increment metric to newrelic
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.INCREMENT_METRIC}`, {
                name: 'Document/sendMessage/fail',
                value: 1,
            });
            const endTimeInSecond = moment().valueOf() / 1000;
            const apiResponseTime = endTimeInSecond - startTimeInSecond;
            // Set sending status is done
            setWaiting(false);
            // Reset response content to be empty
            setResContent('');
            // Save the error to the system log
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Chat, sendMessage, ${err === null || err === void 0 ? void 0 : err.message}, ${aiModel}, ${(_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status}, ${(_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.type}, ${apiResponseTime}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
            // Check if the error is from axios
            if ((_c = err === null || err === void 0 ? void 0 : err.response) === null || _c === void 0 ? void 0 : _c.status) {
                // If yes, notify user with the server error message
                toast.error((_e = (_d = err === null || err === void 0 ? void 0 : err.response) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.message);
                return undefined;
            }
            ;
            toast.error(t('Something went wrong. We are figuring it out.'));
            return undefined;
        }
        ;
    }), [chat, outputLanguage]);
    const [/** Show/hide editor */ showEditor, setShowEditor] = useState(false);
    const [/** Show/hide workflows */ showWorkflow, setShowWorkflow] = useState(false);
    /**
     * Handle Show/hide editor
     */
    const handleShowEditorClick = () => {
        setShowEditor(true);
        setShowWorkflow(false);
    };
    /**
     * Handle show workflow click
     */
    const handleShowWorkflowClick = useMemo(() => (() => {
        setShowWorkflow(true);
        setShowEditor(false);
    }), []);
    /**
     * Handle hide both editor and workflow
     */
    const handleHideEditorAndWorkflowClick = () => {
        setShowEditor(false);
        setShowWorkflow(false);
    };
    const [
    /** Workflow data */ workflow, setWorkflow,] = useState(Object.assign({}, DEFAULT_WORKFLOW));
    const [
    /** Workflow Open/Close */ workflowOpen, setWorkflowOpen,] = useState(false);
    return (_jsxs(Box, Object.assign({ id: 'document-container', sx: documentStyles.container }, { children: [_jsx(ChatTitle, { chat: chat, setChat: setChat, isDownMDBreakPoint: isDownMDBreakPoint, sx: {
                    position: 'relative',
                    top: isDownMDBreakPoint ? '-58px' : '-48px',
                    left: isDownMDBreakPoint ? '48px' : '-8px',
                    width: user ? (isDownMDBreakPoint ? 'calc(100% - 104px)' : 'calc(100% - 96px)') : (isDownMDBreakPoint ? 'calc(100% - 152px)' : 'calc(100% - 176px)'),
                    maxWidth: isDownMDBreakPoint ? '320px' : 'auto',
                    alignItems: 'left',
                    flexDirection: isDownMDBreakPoint ? 'column' : 'row',
                    justifyContent: isDownMDBreakPoint ? 'center' : 'space-between',
                    display: 'inline-flex',
                    flexBasis: 'auto',
                    flexShrink: '1',
                    zIndex: '1202',
                } }), _jsxs(Grid, Object.assign({ container: true, columnSpacing: { md: 3, xs: 0 }, sx: {
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                    alignItems: 'stretch',
                    boxSizing: 'border-box',
                    position: 'relative',
                    top: isDownMDBreakPoint ? '-58px' : '-32px',
                } }, { children: [_jsxs(Grid, Object.assign({ item: true, md: showEditor || showWorkflow ? 6 : 12, sx: {
                            boxSizing: 'border-box',
                            height: '100%',
                            maxWidth: {
                                //md: showEditor || showWorkflow ? '30rem' : '57.5rem',
                                //lg: showEditor || showWorkflow ? '46rem' : '57.5rem',
                                //lg: '100%',
                                md: showEditor || showWorkflow ? '100%' : '57.5rem',
                                xs: showEditor || showWorkflow ? '0' : '100%',
                            },
                            transition: 'max-width 0.1s ease-out',
                            width: '100%',
                            position: 'relative',
                            /*
                            display: {
                              md: 'inline-flex',
                              xs: showEditor ? 'none' : 'inline-flex'
                            },
                            */
                        } }, { children: [_jsx(Chat, { documentId: documentId, outputLanguage: outputLanguage, chat: chat, setChat: setChat, editorTitle: editor === null || editor === void 0 ? void 0 : editor.title, sendMessage: sendMessage, handleDocumentUpdate: handleDocumentUpdate, waiting: waiting, resContent: resContent, showEditor: showEditor || showWorkflow, isDownMDBreakPoint: isDownMDBreakPoint, setWorkflowOpen: setWorkflowOpen, setWorkflow: setWorkflow, handleShowWorkflowClick: handleShowWorkflowClick }), _jsx(IconButton, Object.assign({ sx: {
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translate(0%, -50%)',
                                    display: (showEditor || showWorkflow) ? 'inline-flex' : 'none',
                                    right: isDownMDBreakPoint ? '-40px' : '-1.375rem',
                                    borderRadius: isDownMDBreakPoint ? '' : '1rem',
                                    backgroundColor: isDownMDBreakPoint ? 'blueLight.main' : 'white.main',
                                    padding: isDownMDBreakPoint ? '' : '0.875rem 0.25rem',
                                    zIndex: '10',
                                    color: 'black.main',
                                }, onClick: handleHideEditorAndWorkflowClick }, { children: _jsx(ArrowForwardIos, { sx: { fontSize: isDownMDBreakPoint ? '24px' : '12px' } }) }))] })), _jsxs(Grid, Object.assign({ item: true, xs: true, md: 6, sx: {
                            boxSizing: 'border-box',
                            margin: '0',
                            display: {
                                xs: (showEditor || showWorkflow) ? 'inline-flex' : 'none',
                            },
                            transition: 'max-width 0.1s ease-out',
                            maxWidth: (showEditor || showWorkflow) ? '128rem' : '0',
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                            //marginLeft: isDownMDBreakPoint ? '0' : ((showEditor || showWorkflow) ? '25px' : '0'),
                        } }, { children: [_jsx(Box, Object.assign({ sx: { display: showEditor ? 'inline-flex' : 'none', width: '100%', height: '100%' } }, { children: _jsx(Editor, { documentId: documentId, handleDocumentUpdate: handleDocumentUpdate, editor: editor, setEditor: setEditor, sendMessage: sendMessage, outputLanguage: outputLanguage }) })), _jsx(Box, Object.assign({ sx: { display: showWorkflow ? 'inline-flex' : 'none', width: '100%', height: '100%' } }, { children: _jsx(Workflows, { sendMessage: sendMessage, workflow: workflow, setWorkflow: setWorkflow, workflowOpen: workflowOpen, setWorkflowOpen: setWorkflowOpen }) }))] })), _jsxs(Box, Object.assign({ sx: {
                            position: 'absolute',
                            top: '50%',
                            transform: 'translate(0%, -50%)',
                            width: 'auto',
                            right: '0',
                        } }, { children: [_jsxs(Box, Object.assign({ sx: { display: isDownMDBreakPoint ? 'none' : 'block' } }, { children: [_jsx(Tooltip, Object.assign({ title: t('Edit content with AI in a canvas'), placement: 'top', slotProps: {
                                            tooltip: {
                                                sx: Object.assign({}, chatStyles.tooltip)
                                            }
                                        } }, { children: _jsx(Box, Object.assign({ sx: { display: showEditor ? 'none' : 'block', margin: '0 0 0.5rem' } }, { children: _jsx(ButtonSG, Object.assign({ variant: "contained", color: "blueLight", sx: {
                                                    borderRadius: '1rem',
                                                    padding: '0.5rem 1rem',
                                                    boxShadow: 'none',
                                                }, startIcon: _jsx(ArrowBackIosNew, { sx: { width: '16px', color: 'blackDark.main' } }), onClick: handleShowEditorClick }, { children: _jsx(EditIconSG, { sx: { color: 'blackDark.main', width: '1.5rem', height: '1.5rem' } }) })) })) })), _jsx(Tooltip, Object.assign({ title: t('Run AI workflows'), placement: 'bottom', slotProps: {
                                            tooltip: {
                                                sx: Object.assign({}, chatStyles.tooltip)
                                            }
                                        } }, { children: _jsx(Box, Object.assign({ sx: { display: showWorkflow ? 'none' : 'block', } }, { children: _jsx(ButtonSG, Object.assign({ variant: "contained", color: "blueLight", sx: {
                                                    borderRadius: '1rem',
                                                    padding: '0.5rem 1rem',
                                                    boxShadow: 'none',
                                                }, startIcon: _jsx(ArrowBackIosNew, { sx: { width: '16px', color: 'blackDark.main' } }), onClick: handleShowWorkflowClick }, { children: _jsx(WorkflowIconSG, { sx: { color: 'blackDark.main', width: '1.5rem', height: '1.5rem' } }) })) })) }))] })), _jsxs(Box, Object.assign({ sx: {
                                    display: isDownMDBreakPoint ? 'block' : 'none',
                                } }, { children: [_jsx(Tooltip, Object.assign({ title: t('Edit content with AI in a canvas'), placement: 'top', slotProps: {
                                            tooltip: {
                                                sx: Object.assign({}, chatStyles.tooltip)
                                            }
                                        } }, { children: _jsx(Box, Object.assign({ sx: { display: showEditor ? 'none' : 'block', margin: '0 0 0.5rem' } }, { children: _jsx(IconButton, Object.assign({ sx: Object.assign({}, documentStyles.mobileIconButton), onClick: handleShowEditorClick }, { children: _jsx(EditIconSG, { sx: { fontSize: '24px' } }) })) })) })), _jsx(Tooltip, Object.assign({ title: t('Run AI workflows'), placement: 'bottom', slotProps: {
                                            tooltip: {
                                                sx: Object.assign({}, chatStyles.tooltip)
                                            }
                                        } }, { children: _jsx(Box, Object.assign({ sx: { display: showWorkflow ? 'none' : 'block' } }, { children: _jsx(IconButton, Object.assign({ sx: Object.assign({}, documentStyles.mobileIconButton), onClick: handleShowWorkflowClick }, { children: _jsx(WorkflowIconSG, { sx: { fontSize: '24px' } }) })) })) }))] }))] }))] }))] })));
};
